import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { getVariant, track } from '../lib/paywall';
import { useAuth } from './AuthContext';
import { useLAScope } from './LAScopeContext';

const EntitlementsContext = createContext(null);

const FREE_DEFAULTS = {
  plan_id: 'free',
  features: {
    map_view: true, business_search: true, business_profile: true, analytics_read: true,
    ai_query: true,
    ai_response_download: false, intervention_planner: false, economic_impact_estimator: false,
    priority_matrix: false, report_generation: false, pdf_export: false,
    csv_export: false, council_level_report: false, sensitivity_analysis: false,
    api_access: false, multi_authority: false, white_label_export: false,
  },
  pdf_profile: { plan: 'free', dpi: 0, locked: true, watermark: false, max_pages: 0, footer: '' },
  ai_prompts_limit: 2, ai_prompts_used_today: 0, ai_prompt_credits: 0,
  reports_limit: 0, reports_used: 0, report_credits: 0,
  is_daily_metered: true,
};

export function EntitlementsProvider({ children }) {
  const { user } = useAuth();
  const { laScope, setLAScope } = useLAScope();
  const [ent, setEnt] = useState(null);
  const [variant, setVariant] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setEnt(FREE_DEFAULTS);
      const v = await getVariant();
      setVariant(v);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/billing/entitlements');
      setEnt(data);
      if (data.district_licence && !sessionStorage.getItem('adoptic_scope_touched') && laScope !== data.district_licence) {
        setLAScope(data.district_licence);
      }
      const v = await getVariant(data.paywall_variant);
      setVariant(v);
    } catch {
      setEnt(FREE_DEFAULTS);
      setVariant(await getVariant());
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when user or LA scope changes
  }, [user, laScope]);

  useEffect(() => { refresh(); }, [refresh]);

  const has = (feature) => Boolean(ent?.features?.[feature]);
  const planLabel = (p) => ({
    free: 'Free', pro: 'Pro', premium: 'Premium', enterprise: 'Enterprise',
  }[p] || p);

  return (
    <EntitlementsContext.Provider value={{
      ent: ent || FREE_DEFAULTS, loading, refresh, has, planLabel,
      variant: variant || { key: 'control', banner_copy: "You're viewing a Pro feature preview", headline_copy: 'Pro feature: Economic Return + Ranked Businesses', cta_copy: 'Upgrade to Pro to unlock' },
      track,
    }}>
      {children}
    </EntitlementsContext.Provider>
  );
}

export const useEntitlements = () => useContext(EntitlementsContext);
